import { useCallback, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { PERMISSION_ACTION_EDIT, PERMISSION_ENTITY_PAYMENT, ROUTE_PAYABLE, ROUTE_PAYABLES } from '@klear/constants'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { useTrader } from '@klear/ui/hooks/use-trader'
import toInteger from 'lodash/toInteger'
import PayableWizard from '@/app/payable/workflow/PayableWizard'
import type { PayableWizardMode } from '@/app/payable/workflow/usePayableWizard'

export type PayableWizardPageProps = {
  mode: PayableWizardMode
}

export default function PayableWizardPage({ mode }: PayableWizardPageProps) {
  const { traderId, payableId, orderId } = useParams<{
    traderId?: string
    payableId?: string
    orderId?: string
  }>()
  const navigate = useNavigate()
  const { urlPrefixApp } = useTrader(traderId)
  const { user } = useAuth()

  const tid = toInteger(traderId)
  const parsedPayableId = payableId ? toInteger(payableId) : undefined
  const orderIdSeed = orderId ? toInteger(orderId) : null
  const base = `${urlPrefixApp}/${ROUTE_PAYABLE}`
  const canEditPayable = !!user?.hasPermission(PERMISSION_ENTITY_PAYMENT, PERMISSION_ACTION_EDIT)

  const exitToList = useCallback(() => navigate(`${urlPrefixApp}/${ROUTE_PAYABLES}`), [navigate, urlPrefixApp])

  const [editingInPlace, setEditingInPlace] = useState(false)
  const startEditing = useCallback(() => setEditingInPlace(true), [])
  const stopEditing = useCallback(() => setEditingInPlace(false), [])

  const goToView = useCallback(() => {
    if (parsedPayableId) navigate(`${base}/${parsedPayableId}`)
  }, [base, navigate, parsedPayableId])

  return (
    <PayableWizard
      mode={mode}
      traderId={tid}
      traderIdParam={traderId ?? ''}
      payableId={parsedPayableId}
      orderIdSeed={orderIdSeed}
      canEdit={mode === 'view' ? canEditPayable : true}
      editable={mode === 'view' && editingInPlace}
      onEdit={mode === 'view' && parsedPayableId ? startEditing : undefined}
      onDoneEditing={mode === 'view' ? stopEditing : parsedPayableId ? goToView : undefined}
      onBack={exitToList}
    />
  )
}
